import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { AuthService } from '@echospaces-web-monorepo/es-auth';
import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export type CompanyMemberRole = 'super-admin' | 'editors' | 'staff';

export interface OrgMember {
  userId: string;
  email: string;
  fullName?: string;
  roles: ReadonlyArray<CompanyMemberRole>;
}

export interface AddMemberDto {
  email: string;
  fullName?: string;
  roles: ReadonlyArray<CompanyMemberRole>;
}

export interface AddMemberResult {
  userId: string;
  email: string;
  /**
   * True when a new Keycloak user was created and Keycloak emailed them an
   * invite (verify email + set password). False when the address already
   * existed in the realm — that person keeps their existing credentials and is
   * simply added to this org.
   */
  invited: boolean;
  roles: ReadonlyArray<CompanyMemberRole>;
}

/**
 * Organization-scoped user directory — listing + client-side search of members
 * of the caller's company. Backed by the portal facade SAME-ORIGIN at
 * `GET /api/portal/companies/:orgId/members`, which proxies to the CC member API
 * server-side (the silo holds no member state). cms admin-only on the CC.
 *
 * Re-used wherever the UI needs to resolve a user inside the current org:
 * the OrgUserPicker (AddMemberModal, role-assignment flows), the users
 * page, and future delegation flows. Loads lazily and caches per session
 * — call `refresh()` to invalidate.
 */
@Injectable({ providedIn: 'root' })
export class CompanyMembersService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);
  private readonly auth = inject(AuthService);

  private readonly _members = signal<OrgMember[]>([]);
  private readonly _loading = signal(false);
  private readonly _loaded = signal(false);

  readonly members = this._members.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  /**
   * Returns members whose email or fullName starts with / contains `query`
   * (case-insensitive). Empty query returns the full list. Limits to
   * `max` matches to keep dropdowns responsive on large orgs.
   */
  search(query: string, max = 20): OrgMember[] {
    const q = query.trim().toLowerCase();
    const all = this._members();
    if (!q) return all.slice(0, max);
    return all
      .filter((m) =>
        m.email.toLowerCase().includes(q)
        || (m.fullName?.toLowerCase().includes(q) ?? false))
      .slice(0, max);
  }

  /**
   * Returns the member directory, loading it on first call. Subsequent
   * calls are no-ops unless `refresh()` was called.
   */
  async ensureLoaded(): Promise<void> {
    if (this._loaded() || this._loading()) return;
    await this.load();
  }

  async refresh(): Promise<void> {
    await this.load();
  }

  findById(userId: string): OrgMember | undefined {
    return this._members().find((m) => m.userId === userId);
  }

  /**
   * Add a new member to the caller's organization. Returns the upstream
   * response — `invited` is true when a new Keycloak user was created and
   * Keycloak mailed them an invite. No password is ever returned: credentials
   * are set by the invitee on Keycloak's own screens.
   *
   * Cache is refreshed so subsequent picker / list reads include the
   * new member without an explicit `refresh()`.
   */
  async addMember(dto: AddMemberDto): Promise<AddMemberResult> {
    const orgId = this.requireOrgId();
    const result = await lastValueFrom(this.http.post<AddMemberResult>(`${this.api}/companies/${orgId}/members`, dto));
    await this.refresh();
    return result;
  }

  /**
   * Replace a member's role set. The server effectively-merges `staff`
   * (every member always has `staff`); pass only the opt-in roles.
   */
  async setMemberRoles(userId: string, roles: ReadonlyArray<CompanyMemberRole>): Promise<OrgMember> {
    const orgId = this.requireOrgId();
    const result = await lastValueFrom(this.http.patch<OrgMember>(`${this.api}/companies/${orgId}/members/${userId}/roles`, { roles }));
    await this.refresh();
    return result;
  }

  async removeMember(userId: string): Promise<void> {
    const orgId = this.requireOrgId();
    await lastValueFrom(this.http.delete(`${this.api}/companies/${orgId}/members/${userId}`));
    this._members.update((list) => list.filter((m) => m.userId !== userId));
  }

  private requireOrgId(): string {
    const orgId = this.auth.user()?.organizationId;
    if (!orgId) {
      throw new Error('No organizationId on the auth user — sign-in incomplete');
    }
    return orgId;
  }

  private async load(): Promise<void> {
    const orgId = this.auth.user()?.organizationId;
    if (!orgId) {
      // No org claim yet — bail silently; consumers should re-call after auth
      // settles. A hard error here would spam logs while the OAuth flow finishes.
      return;
    }
    this._loading.set(true);
    try {
      const data = await lastValueFrom(this.http.get<OrgMember[]>(`${this.api}/companies/${orgId}/members`));
      this._members.set(data);
      this._loaded.set(true);
    } finally {
      this._loading.set(false);
    }
  }
}
